import { useNavigate } from "react-router-dom";
import { ReplyButton } from "../../model/ReplyButton";
import Button from "../button/Button";
import { useCallback, useEffect, useMemo, useState } from "react";
import messagesQuery from "../../firestore/Messages.query";
import { GridCol } from "../table/MessageInfoTableList";
import { MessagingDeliveryDestinationController } from "../../controller/MessagingDeliveryDestinationController";
import { Company } from "../../model/Companies";
import { MessageDeliver, Messages, RdbMessageDeliverMapping } from "../../model/Message";
import { auth } from "../../firebase";
import TableList from "../table/TableList";
import { requestRdbMessagesDeliver } from "../../api/Messages.api";
import { Spiner } from "../spinner/Spiner";
import { useDispatch } from "react-redux";
import { alertOpen, AlertValue } from "../../features/alert.slice";

export type DeliveryResultTallyProp = {
  messageId: string;
  replyButton?: ReplyButton;
  company?: Company;
  minWidth?: string;
};

export const DeliveryResultTally = ({
  messageId,
  replyButton,
  company,
  minWidth = ''
}: DeliveryResultTallyProp) => {
  const currentUser = auth.currentUser;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const messageDeliveryDestination = new MessagingDeliveryDestinationController();

  const [isLoad, setIsLoad] = useState<boolean>(true);
  const [message, setMessage] = useState<Messages | null>(null);
  const [publishActiveAccount, setPublishActiveAccount] = useState<number>(0);
  const [publishInactiveAccount, setPublishInactiveAccount] = useState<number>(0);
  const [readedCount, setReadedCount] = useState<number>(0);
  const [messageDeliverList, setMessageDeliverList] = useState<MessageDeliver[]>([]);

  const [gridRowLength, setGridRowLength] = useState<number>(0);
  const [selectPage, setSelectPage] = useState<number>(1);
  const [selectNumber, setSelectNumber] = useState(50);
  const [firstPageFlg, setFirstPageFlg] = useState(false);

  useEffect(() => {
    const fetchMessage = async () => {
      if (messageId) {
        // お知らせ
        const message = await messagesQuery.getMessage(messageId);
        setMessage(message);
        setFirstPageFlg(true);
      }
    };
    fetchMessage();
  }, [messageId]);

  // 配信先一覧を取得する共通処理
  useEffect(() => {
    const f = async () => {
      if (company && message) {
        const result = await requestRdbMessagesDeliver(
          currentUser!.tenantId ?? '',
          message.id,
          message.select ?? '',
          selectNumber.toString(),
          selectPage.toString()
        );
        if (result.success) {
          const deliverList = result.data.rows.map((d: any) => RdbMessageDeliverMapping(d));
          setMessageDeliverList(deliverList);
          setGridRowLength(result.data.count.total_users);
          setPublishActiveAccount(result.data.count.active_users);
          setPublishInactiveAccount(result.data.count.inactive_users);
          setReadedCount(result.data.count.read_users);
        } else {
          console.log(result.data)
          dispatch(alertOpen(AlertValue("配信先一覧の取得に失敗しました。", "danger")));
        }
        setIsLoad(false);
      }
    }
    f();
  }, [company, message, selectPage, selectNumber]);

  const rowData = useMemo(() => messageDeliverList.map((data: MessageDeliver) => {
    let replyState;
    let checkState;
    if (data.replyData !== '') {
      let displayName = data.replyData;
      if (message?.eventFlg && displayName === "会場で参加") {
        displayName = "会場";
      } else if (message?.eventFlg && displayName === "オンラインで参加") {
        displayName = "オンライン";
      }
      replyState = <span key={`badge-${data.userId}-${displayName}`}>{displayName}</span>;
    } else {
      replyState = <span key={`badge-${data.userId}-未回答`}>未回答</span>;
    }

    return {
      rowData: {
        userId: data.userId,
        checkbox: checkState,
        readed: data.readed === '' ? (<></>) : (data.readed === 'TRUE' ? <span className={"text-green-700 font-bold"}>既読</span> : <span className={"text-red-700 font-bold"}>未読</span>),
        name: `${data.lastName}`,
        account: data.account === '利用可' ? <span className={"text-green-700 font-bold"}>{data.account}</span> : <span className={"text-red-700 font-bold"}>{data.account}</span> ,
        reply: (<>{replyState}</>),
        replyData: data.replyData,
        group: data.groups,
      },
      sortId: data.userId || "", // ソート用のIDを追加
      style: `sm:whitespace-normal`
    };
  }), [message, messageDeliverList]);

  // 返信ボタン結果集計のカラム
  const gridCol: GridCol[] = messageDeliveryDestination.deliveryGridCol;

  const selectNumberList =
    [
      { id: 50, name: "50" },
      { id: 100, name: "100" },
      { id: 200, name: "200" },
    ];

  // ページング処理
  const paging = useCallback((page: number) => {
    setFirstPageFlg(false);
    setSelectPage(page);
  }, []);

  // 表示件数変更時に1ページ目を表示する
  useEffect(() => {
    setFirstPageFlg(true);
  }, [selectNumber])

  // 返信ボタン結果集計
  const deliverListCount = useMemo(() => 
    <div className="flex mt-3 ml-2 justify-start gap-4">
      <span>既読数：{readedCount}名</span>
      <span>未読数：{publishActiveAccount - readedCount}名</span>
      <span>利用不可：{publishInactiveAccount}名</span>
    </div>
  , [publishActiveAccount, publishInactiveAccount, readedCount]);

  return (
    <>
      <div className="flex py-2">
        <div className="flex flex-1 flex-col pr-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xl font-bold text-gray-900">配信先一覧　</h3>
            {/* 返信ボタンが設定されている場合 */}
              <div className="flex space-x-2">
                <Button size="sm" 
                  onClick={() => navigate(`/message/${messageId}/reply`)}
                  disabled={!(
                      replyButton?.id !== "undefined" &&
                      replyButton !== undefined
                    )}>
                  返信一覧
                </Button>
                {message?.eventFlg && (
                  <Button
                    size="sm"
                    onClick={() => navigate(`/message/${messageId}/attendance`)}
                  >
                    出欠一覧
                  </Button>
                )}
                {message?.eventFlg && (
                  <Button
                    size="sm"
                    disabled={!(
                      message?.questionnaireFlg &&
                      message?.eventEndAt &&
                      new Date() > new Date(message.eventEndAt)
                    )}
                    className={!(
                      message?.questionnaireFlg &&
                      message?.eventEndAt &&
                      new Date() > new Date(message.eventEndAt)
                    ) ? "opacity-50 cursor-not-allowed" : ""}
                    onClick={() => navigate(`/message/${messageId}/questionnaireResults`)}
                  >
                    アンケート結果
                  </Button>
                )}
                {message?.eventFlg && (
                  <Button
                    size="sm"
                    disabled={!message?.municipalityConfirmedFlg}
                    className={!message?.municipalityConfirmedFlg ? "opacity-50 cursor-not-allowed" : ""}
                    onClick={() => navigate(`/eventParticipation/${messageId}`)}
                  >
                    イベント参加履歴一覧
                  </Button>
                )}
              </div>
          </div>
          {deliverListCount}
        </div>
      </div>
      <div className="mb-5">
        {isLoad ?
          <Spiner />
          :
          <TableList
            header
            footer={false}
            gridCol={gridCol}
            gridRow={rowData}
            gridRowLength={gridRowLength}
            limit={selectNumber}
            selectNumberList={selectNumberList}
            setSelectNumber={setSelectNumber}
            pagingFunc={paging}
            firstPageFlg={firstPageFlg}
            setFirstPageFlg={setFirstPageFlg}
            minWidth={minWidth !== '' ? minWidth : "min-w-[900px]"}
            fixedHeight={true}
          />
        }
      </div>
    </>
  );
};
